import apiClient from './apiClient';

export type PurchaseOrderStatus = 'draft' | 'sent' | 'partially_received' | 'received' | 'cancelled';

export interface PurchaseOrderItemDto {
  id: string;
  productId: string;
  productName: string;
  quantityOrdered: number;
  quantityReceived: number;
  unitPrice: number;
  lineTotal: number;
}

export interface PurchaseOrderDto {
  id: string;
  orderNumber: string;
  supplierId: string;
  supplierName: string;
  status: PurchaseOrderStatus;
  locationName: string;
  notes: string;
  totalAmount: number;
  items: PurchaseOrderItemDto[];
  sentAt: string | null;
  receivedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PoLineInput {
  productId: string;
  quantityOrdered: number;
  unitPrice: number;
}

export interface ReceiveLineInput {
  purchaseOrderItemId: string;
  quantity: number;
  batchNumber: string;
  expirationDate: string;
  unitCost: number;
}

function errMessage(e: unknown): string {
  const ax = e as { response?: { data?: { error?: string; message?: string } } };
  return ax.response?.data?.error || ax.response?.data?.message || (e instanceof Error ? e.message : 'Eroare');
}

export async function fetchPurchaseOrders(clinicId: string): Promise<PurchaseOrderDto[]> {
  const res = await apiClient.get(`/purchase-orders?clinicId=${encodeURIComponent(clinicId)}`);
  if (!res.data?.success) throw new Error(res.data?.error || 'Lista comenzi esuata');
  return res.data.data?.orders ?? [];
}

export async function getPurchaseOrder(clinicId: string, id: string): Promise<PurchaseOrderDto> {
  const res = await apiClient.get(`/purchase-orders/${id}?clinicId=${encodeURIComponent(clinicId)}`);
  if (!res.data?.success) throw new Error(res.data?.error || 'Detaliu comanda esuat');
  return res.data.data?.order;
}

export async function createPurchaseOrder(
  clinicId: string,
  body: {
    supplierId: string;
    locationName?: string;
    notes?: string;
    items: PoLineInput[];
  }
): Promise<PurchaseOrderDto> {
  const res = await apiClient.post('/purchase-orders', { ...body, clinicId });
  if (!res.data?.success) throw new Error(res.data?.error || 'Creare comanda esuata');
  return res.data.data?.order;
}

export async function updatePurchaseOrderDraft(
  clinicId: string,
  id: string,
  body: {
    supplierId?: string;
    locationName?: string;
    notes?: string;
    items?: PoLineInput[];
  }
): Promise<PurchaseOrderDto> {
  const res = await apiClient.patch(`/purchase-orders/${id}`, { ...body, clinicId });
  if (!res.data?.success) throw new Error(res.data?.error || 'Actualizare comanda esuata');
  return res.data.data?.order;
}

export async function sendPurchaseOrder(clinicId: string, id: string): Promise<PurchaseOrderDto> {
  const res = await apiClient.post(`/purchase-orders/${id}/send`, { clinicId });
  if (!res.data?.success) throw new Error(res.data?.error || 'Trimitere comanda esuata');
  return res.data.data?.order;
}

export async function cancelPurchaseOrder(clinicId: string, id: string): Promise<PurchaseOrderDto> {
  const res = await apiClient.delete(`/purchase-orders/${id}`, { data: { clinicId } });
  if (!res.data?.success) throw new Error(res.data?.error || 'Anulare comanda esuata');
  return res.data.data?.order;
}

export async function receivePurchaseOrder(
  clinicId: string,
  id: string,
  lines: ReceiveLineInput[]
): Promise<PurchaseOrderDto> {
  const res = await apiClient.post(`/purchase-orders/${id}/receive`, { clinicId, lines });
  if (!res.data?.success) throw new Error(res.data?.error || 'Receptie esuata');
  return res.data.data?.order;
}

export interface PurchaseOrderAttachmentDto {
  id: string;
  purchaseOrderId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
  url: string;
  uploadedBy: string | null;
  createdAt: string;
}

export async function fetchPurchaseOrderAttachments(clinicId: string, poId: string): Promise<PurchaseOrderAttachmentDto[]> {
  const res = await apiClient.get(`/purchase-orders/${poId}/attachments?clinicId=${encodeURIComponent(clinicId)}`);
  if (!res.data?.success) throw new Error(res.data?.error || 'Lista atasamente esuata');
  return res.data.data?.attachments ?? [];
}

export async function uploadPurchaseOrderAttachment(clinicId: string, poId: string, file: File): Promise<PurchaseOrderAttachmentDto> {
  const form = new FormData();
  form.append('file', file);
  form.append('clinicId', clinicId);
  const res = await apiClient.post(`/purchase-orders/${poId}/attachments`, form);
  if (!res.data?.success) throw new Error(res.data?.error || 'Upload atasament esuat');
  return res.data.data?.attachment;
}

export async function deletePurchaseOrderAttachment(clinicId: string, poId: string, attachmentId: string): Promise<void> {
  const res = await apiClient.delete(`/purchase-orders/${poId}/attachments/${attachmentId}`, { data: { clinicId } });
  if (!res.data?.success) throw new Error(res.data?.error || 'Stergere atasament esuata');
}

export { errMessage as purchaseOrderServiceErrorMessage };
